import { SocketSessionError } from '@common/sockets/enums/socket-session-error.enum';
import { SessionOptions, SocketSession } from '@common/sockets/socket-session';
import { ReviewApplySessionErrorEvent } from '@common/sockets/types/review-apply-session-error-event.type';
import { isEnumValue } from '@common/util/enum';
import { ManagerOptions, SocketOptions } from 'socket.io-client';

export interface ReviewApplySessionOptions extends SessionOptions {
  reviewPackageId: number;
}

/**
 * Connects to the collab-workspace socket server's review-apply room (identified by `reviewPackageId`
 * instead of `projectId`/`branchName`) to receive live progress for a single review package apply
 * operation. Re-emits the `review.apply.*` events collab-workspace forwards from collab-pull's pub/sub.
 */
export class ReviewApplySession extends SocketSession<ReviewApplySessionOptions, ReviewApplySessionErrorEvent> {
  public reviewPackageId: number;

  constructor(options: ReviewApplySessionOptions) {
    super(options);

    this.reviewPackageId = options.reviewPackageId ?? null;
  }

  protected override createSocketOptions(socketUrl: URL): Partial<ManagerOptions & SocketOptions> {
    return {
      ...super.createSocketOptions(socketUrl),
      query: {
        reviewPackageId: this.reviewPackageId
      }
    }
  }

  protected override parseError(event: string, error: any): ReviewApplySessionErrorEvent {
    if ((event === 'connect_error' || event === 'reconnect_error') &&
      error.data && isEnumValue(SocketSessionError, error.data.name)) {
      return { type: error.data.name as SocketSessionError, error: error.data };
    }

    return { type: SocketSessionError.UnableToReachServer, error };
  }

  /** Creates a session with the server by opening a socket connection and setting up event handlers. */
  public start() {
    super.start();

    this.socket.on('review.apply.started', data => {
      this.emit('review.apply.started', data);
    });
    this.socket.on('review.apply.progress', data => {
      this.emit('review.apply.progress', data);
    });
    this.socket.on('review.apply.completed', data => {
      this.emit('review.apply.completed', data);
    });
    this.socket.on('review.apply.failed', data => {
      this.emit('review.apply.failed', data);
    });
    this.socket.on('review.apply.file-completed', data => {
      this.emit('review.apply.file-completed', data);
    });
  }
}
